import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ChapterProgressAccordion = ({ chapters, onNavigateToChapter }) => {
  const [expandedChapter, setExpandedChapter] = useState(null);

  const toggleChapter = (chapterId) => {
    setExpandedChapter(expandedChapter === chapterId ? null : chapterId);
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in-progress': return 'text-warning bg-warning/10';
      case 'not-started': return 'text-text-secondary bg-border-light';
      default: return 'text-text-secondary bg-border-light';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle2';
      case 'in-progress': return 'Clock';
      case 'not-started': return 'Circle';
      default: return 'Circle';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-primary';
    if (score >= 40) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="space-y-4">
      {chapters.map((chapter) => (
        <div key={chapter.id} className="overflow-hidden border rounded-lg bg-surface border-border">
          <button
            onClick={() => toggleChapter(chapter.id)}
            className="flex items-center justify-between w-full p-4 text-left hover:bg-border-light nav-transition"
          >
            <div className="flex items-center space-x-4">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${getStatusColor(chapter.status)}`}>
                <Icon name={getStatusIcon(chapter.status)} size={20} />
              </div>
              <div>
                <h3 className="font-semibold font-heading text-text-primary">{chapter.title}</h3>
                <p className="text-sm text-text-secondary">{chapter.description}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm font-medium text-text-primary">{Math.round(chapter.progress)}%</div>
                <div className="text-xs text-text-secondary">{chapter.status}</div>
              </div>
              <Icon
                name={expandedChapter === chapter.id ? 'ChevronUp' : 'ChevronDown'}
                size={20}
                className="text-text-secondary"
              />
            </div>
          </button>

          {expandedChapter === chapter.id && (
            <div className="p-4 border-t border-border bg-background content-reveal">
              <div className="grid grid-cols-1 gap-4 mb-4 md:grid-cols-2 lg:grid-cols-3">
                {/* Presentations */}
                <div className="space-y-2">
                  <h4 className="flex items-center space-x-2 font-medium text-text-primary">
                    <Icon name="Play" size={16} />
                    <span>Presentations</span>
                  </h4>
                  {chapter.presentations.map((presentation) => (
                    <div key={presentation.id} className="flex items-center justify-between p-2 border rounded bg-surface">
                      <div className="flex items-center space-x-2">
                        <Icon
                          name={presentation.completed ? 'CheckCircle2' : 'Circle'}
                          size={16}
                          className={presentation.completed ? 'text-success' : 'text-text-secondary'}
                        />
                        <span className="text-sm text-text-primary">{presentation.title}</span>
                      </div>
                      <span className="text-xs text-text-secondary">{presentation.duration}</span>
                    </div>
                  ))}
                </div>

                {/* Quizzes */}
                <div className="space-y-2">
                  <h4 className="flex items-center space-x-2 font-medium text-text-primary">
                    <Icon name="FileQuestion" size={16} />
                    <span>Quizzes</span>
                  </h4>
                  {chapter.quizzes.map((quiz) => (
                    <div key={quiz.id} className="flex items-center justify-between p-2 border rounded bg-surface">
                      <div className="flex items-center space-x-2">
                        <Icon
                          name={quiz.completed ? 'CheckCircle2' : 'Circle'}
                          size={16}
                          className={quiz.completed ? 'text-success' : 'text-text-secondary'}
                        />
                        <span className="text-sm text-text-primary">{quiz.title}</span>
                      </div>
                      {quiz.score !== null && (
                        <span className={`text-xs font-medium ${getScoreColor(quiz.score)}`}>
                          {quiz.score}%
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Achievements */}
                <div className="space-y-2">
                  <h4 className="flex items-center space-x-2 font-medium text-text-primary">
                    <Icon name="Award" size={16} />
                    <span>Achievements</span>
                  </h4>
                  {chapter.achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-center p-2 space-x-2 border rounded bg-surface">
                      <Icon
                        name={achievement.earned ? 'Medal' : 'Circle'}
                        size={16}
                        className={achievement.earned ? 'text-warning' : 'text-text-secondary'}
                      />
                      <span className={`text-sm ${achievement.earned ? 'text-text-primary' : 'text-text-secondary'}`}>
                        {achievement.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chapter Stats */}
              <div className="grid grid-cols-2 gap-4 p-4 mb-4 rounded-lg md:grid-cols-4 bg-surface">
                <div className="text-center">
                  <div className="text-lg font-bold text-text-primary">{formatTime(chapter.timeSpent || 0)}</div>
                  <div className="text-xs text-text-secondary">Time Spent</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-text-primary">{Math.round(chapter.averageScore)}%</div>
                  <div className="text-xs text-text-secondary">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-text-primary">{chapter.attempts}</div>
                  <div className="text-xs text-text-secondary">Attempts</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-text-primary">{chapter.mastery}%</div>
                  <div className="text-xs text-text-secondary">Mastery</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  iconName="Play"
                  onClick={() => onNavigateToChapter(chapter.id, 'presentation')}
                >
                  Continue Learning
                </Button>
                {chapter.status === 'completed' && (
                  <Button
                    variant="outline"
                    iconName="RotateCcw"
                    onClick={() => onNavigateToChapter(chapter.id, 'quiz')}
                  >
                    Retake Quiz
                  </Button>
                )}
                <Button
                  variant="ghost"
                  iconName="BarChart3"
                  onClick={() => onNavigateToChapter(chapter.id, 'results')}
                >
                  View Results
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChapterProgressAccordion;